import { extractConversationSignals } from "@/components/conversation/conversationListShared";
import type { Conversation } from "@/types/conversation/conversation";

export const DIGEST_CONVERSATION_LIMIT = 20;

function truncateText(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1).trimEnd() + "…";
}

/**
 * One-click inbox digest: hands the most recent conversations to the Brain
 * assistant so the owner gets a quick read of "what's been happening"
 * without opening each call. Uses the same signal extraction as the rows
 * so the wording matches what they see in the list.
 */
export function buildInboxDigestPrompt(conversations: Conversation[]): string {
  const recent = conversations.slice(0, DIGEST_CONVERSATION_LIMIT);

  const lines = recent.map((conversation) => {
    const signals = extractConversationSignals(conversation);
    const genieName = conversation.agent?.name || "Unknown genie";
    const who = signals.customerLabel ? ` from ${signals.customerLabel}` : "";
    const when = signals.hasValidTime ? signals.timeAgo : "recently";
    const topic = signals.primaryTopic ? ` · ${signals.primaryTopic}` : "";
    const summary = signals.summaryPreview
      ? ` — ${truncateText(signals.summaryPreview, 180)}`
      : "";
    const flags = [
      signals.attentionBadge ? "NEEDS ATTENTION" : null,
      conversation.viewed ? null : "unread",
    ]
      .filter(Boolean)
      .join(", ");
    return `- [${when} · ${signals.channel}${who} · ${genieName}${topic}]${summary}${
      flags ? ` (${flags})` : ""
    }`;
  });

  return [
    `Here are my ${recent.length} most recent genie conversations. Give me a quick digest of my inbox:`,
    ``,
    lines.join("\n"),
    ``,
    `Please tell me:`,
    `1. **What's been happening** — the 2-3 sentence version.`,
    `2. **What needs my attention** — anything escalated, unhappy, or unanswered, most urgent first.`,
    `3. **Patterns** — repeated questions or topics worth acting on.`,
    `4. **One improvement** — the single highest-impact change to my genie based on these conversations.`,
    ``,
    `Keep it short and scannable.`,
  ].join("\n");
}
